import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface CardState {
  id: string;
  isDirty: boolean;
  isSaving: boolean;
  lastSavedAt?: number;
}

@Injectable({ providedIn: 'root' })
export class CardStateService {
  private stateMap: Map<string, CardState> = new Map();
  private state$ = new BehaviorSubject<Map<string, CardState>>(this.stateMap);

  register(cardId: string): void {
    if (!this.stateMap.has(cardId)) {
      this.stateMap.set(cardId, { id: cardId, isDirty: false, isSaving: false });
      this.emit();
    }
  }

  setDirty(cardId: string, isDirty: boolean): void {
    const st = this.ensure(cardId);
    if (st.isDirty !== isDirty) {
      st.isDirty = isDirty;
      this.stateMap.set(cardId, st);
      this.emit();
    }
  }

  setSaving(cardId: string, isSaving: boolean): void {
    const st = this.ensure(cardId);
    if (st.isSaving !== isSaving) {
      st.isSaving = isSaving;
      this.stateMap.set(cardId, st);
      this.emit();
    }
  }

  markSaved(cardId: string): void {
    const st = this.ensure(cardId);
    st.isDirty = false;
    st.isSaving = false;
    st.lastSavedAt = Date.now();
    this.stateMap.set(cardId, st);
    this.emit();
  }

  clear(cardId: string): void {
    if (this.stateMap.has(cardId)) {
      this.stateMap.delete(cardId);
      this.emit();
    }
  }

  reset(): void {
    this.stateMap.clear();
    this.emit();
  }

  isAnyDirty(): boolean {
    for (const st of this.stateMap.values()) {
      if (st.isDirty) return true;
    }
    return false;
  }

  get states$(): Observable<Map<string, CardState>> { return this.state$.asObservable(); }

  private ensure(cardId: string): CardState {
    if (!this.stateMap.has(cardId)) this.register(cardId);
    return this.stateMap.get(cardId)!;
  }

  private emit(): void {
    // Emit a new Map instance to ensure OnPush subscribers are notified
    this.state$.next(new Map(this.stateMap));
  }
}


